import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar, User, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { BLOG_CATEGORIES } from "../data/mock";
import { fetchPost, fetchPosts, imgUrl, isHtmlContent, resolveHtmlImages } from "../lib/api";
import DOMPurify from "dompurify";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import NotFound from "./NotFound";

const formatDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = translations[lang].blogPage;
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchPost(slug)
      .then(async (data) => {
        if (!mounted) return;
        setPost(data);
        const all = await fetchPosts();
        const rel = all.filter((p) => p.slug !== data.slug && p.category === data.category);
        const finalRel = (rel.length ? rel : all.filter((p) => p.slug !== data.slug)).slice(0, 3);
        if (mounted) setRelated(finalRel);
      })
      .catch(() => mounted && setPost(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!post) return <NotFound />;

  const paragraphs = (post.content[lang] || "").split(/\n\s*\n/).filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title[lang],
    description: post.excerpt[lang],
    image: imgUrl(post.image),
    datePublished: post.date,
    author: { "@type": "Organization", name: post.author || "AdalCrea" },
    publisher: { "@type": "Organization", name: "AdalCrea" },
  };

  return (
    <>
      <Seo
        title={post.title[lang]}
        description={post.excerpt[lang]}
        path={`/blog/${post.slug}`}
        lang={lang}
        type="article"
        image={imgUrl(post.image)}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-navy-950 pt-36 pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-amber-400 font-semibold hover:gap-3 transition-all mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </Link>
          <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-400 text-xs font-semibold px-3 py-1">
            {BLOG_CATEGORIES.find((c) => c.id === post.category)?.label[lang] || post.category}
          </span>
          <h1 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
            {post.title[lang]}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDate(post.date, lang)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.read_time} {t.readTime}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-white py-14 lg:py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden mb-10">
            <img src={imgUrl(post.image)} alt={post.title[lang]} className="w-full h-[360px] object-cover" />
          </div>
          <div className="space-y-6">
            {isHtmlContent(post.content[lang]) ? (
              <div
                className="blog-body rich-content"
                data-testid="blog-post-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolveHtmlImages(post.content[lang]), { ADD_ATTR: ["target", "rel"] }) }}
              />
            ) : (
              paragraphs.map((para, i) => (
                <p key={i} className="text-lg text-slate-700 leading-relaxed">
                  {para}
                </p>
              ))
            )}
          </div>

          <div className="mt-12 rounded-2xl bg-navy-900 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-xl text-white">
                {translations[lang].ctaBanner.title}
              </h3>
              <p className="mt-2 text-slate-300 text-sm">{translations[lang].ctaBanner.note}</p>
            </div>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold px-6 py-3 rounded-lg transition-colors shrink-0"
            >
              {translations[lang].ctaBanner.button}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-slate-50 py-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-display font-bold text-2xl text-navy-900 mb-8">{t.related}</h2>
            <div className="grid sm:grid-cols-3 gap-8">
              {related.map((p, i) => (
                <Reveal key={p.id || p.slug} delay={i * 90}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block h-full rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={imgUrl(p.image)}
                        alt={p.title[lang]}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-bold text-base text-navy-900 leading-snug group-hover:text-amber-600 transition-colors">
                        {p.title[lang]}
                      </h3>
                      <p className="mt-2 text-slate-600 text-sm line-clamp-2">{p.excerpt[lang]}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogPost;
